import dayjs from 'dayjs';
import { TakeEffect, takeLatest } from 'redux-saga/effects';
import { createSelector } from 'reselect';
import { CallbackSaga, createGeneralCall } from '@altermaind/core/requests-status-management';
import { IS_MOCK_ENABLED } from '@/_shared/constants/mock';
import { GenericPut } from '@/_shared/types';
import { GenericApiError } from '@/_shared/types/apiError';
import { getCurrentOrganization } from '@/core/state/selectors';
import { API_ORGANIZATIONS_ORGANIZATIONID_EMPLOYEES_IMPORTS_JOBID_RECORDS_RECORDID_PATH_KEY } from '@/swagger/constants/anagraphics-keys';
import {
  API_PUT_EMPLOYEES_IMPORT,
  DD_MM_YYYY_WITH_SLASH_REGEX,
  KEY_PUT_EMPLOYEES_IMPORT,
  YYYY_MM_DD_WITH_DASH_REGEX,
} from '../constants';
import { MOCK_RESPONSE_PUT_EMPLOYEES_IMPORT } from '../mocks';

type GenericPutEmployeesImport = GenericPut<
  typeof API_ORGANIZATIONS_ORGANIZATIONID_EMPLOYEES_IMPORTS_JOBID_RECORDS_RECORDID_PATH_KEY
>;

type RequestPutEmployeesImport = GenericPutEmployeesImport['request']['path'] &
  GenericPutEmployeesImport['request']['body'];

export type CallParamsPutEmployeesImport = Omit<RequestPutEmployeesImport, 'organizationId'> & {
  successSaga?: CallbackSaga;
  errorSaga?: CallbackSaga;
};

type CallResponsePutEmployeesImport = GenericPutEmployeesImport['response']['body'];

type CallErrorPutEmployeesImport = GenericApiError;
type CallSelectorsPutEmployeesImport = {
  updatedEmployee: GenericPutEmployeesImport['response']['body'];
};

export const putEmployeesImportCall = createGeneralCall<
  CallParamsPutEmployeesImport,
  CallResponsePutEmployeesImport,
  CallErrorPutEmployeesImport,
  CallSelectorsPutEmployeesImport
>({
  id: KEY_PUT_EMPLOYEES_IMPORT,
  url:
    ({ payload }) =>
    state => {
      const org = getCurrentOrganization(state);
      return API_PUT_EMPLOYEES_IMPORT(
        org as unknown as string,
        payload.jobId || '',
        payload.recordId,
      );
    },
  paramsSelector:
    ({ payload }) =>
    () => {
      let birthDate;
      if (payload?.birthDate) {
        if (
          DD_MM_YYYY_WITH_SLASH_REGEX.test(payload.birthDate) &&
          dayjs(payload.birthDate, 'DD/MM/YYYY', true).isValid() &&
          dayjs(payload.birthDate, 'DD/MM/YYYY', true).isAfter(dayjs().subtract(100, 'year'))
        ) {
          birthDate = dayjs(payload.birthDate, 'DD/MM/YYYY', true).format('YYYY-MM-DD');
        } else if (YYYY_MM_DD_WITH_DASH_REGEX.test(payload?.birthDate || '')) {
          birthDate = payload?.birthDate; // already in correct format date from BE
        } else if (payload?.birthDate) {
          birthDate = '';
        }
      }
      return {
        ...payload,
        birthDate,
      };
    },
  method: 'put',
  successMock: IS_MOCK_ENABLED() ? MOCK_RESPONSE_PUT_EMPLOYEES_IMPORT : undefined,
  selectors: {
    updatedEmployee: ({ selectors }) => createSelector(selectors.response, res => res),
  },
  cancellable: true,
  takeEffect: takeLatest as unknown as TakeEffect,
  rsmOptions: {
    doubleRun: true,
  },
});
